import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export default class LCDCardWidgetNode extends BaseNode {
    constructor() {
        super();
        this.name = 'LCD Card Widget';
        this.id = 'node_lcd_card_widget';
        this.linkedFeature = 'lcd';
        this.icon = 'CgScreen';

        this.inputs = [
            {
                name: 'Draw Card',
                portId: 'lcd_print_card',
                params: [
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'WIDGET',
                name: 'Widget',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId !== 'WIDGET') return super.getSettingOptions(settingId);
        const project: ProjectConfiguration = this.project;
        const widgets = project.getFeature('lcd')?.getSettingValue('WIDGETS');
        const options: SettingOption[] = [];
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i];
            const type = widget.id;
            if (type !== 'lcd_card_widget') continue;
            options.push({
                name: widget.getSettingValue('NAME'),
                value: widget.getID(),
            });
        }
        return options;
    }


    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const widget = project.getFeature(this.getSettingValue('WIDGET'));

        if (!widget) {
            return blocks;
        }
        const variable = widget.getSettingValue('VARIABLE');
        const color = widget.getSettingValue('COLOR');
        const backgroundColor = widget.getSettingValue('BACKGROUND_COLOR');
        const x = widget.getSettingValue('X');
        const y = widget.getSettingValue('Y');
        const w = widget.getSettingValue('W');
        const h = widget.getSettingValue('H');
        const displayLabel = widget.getSettingValue('DISPLAY_LABEL');
        const font = displayLabel === '1' ? 'LCD_SMALL_FONT' : 'LCD_STD_FONT';
        const label = widget.getSettingValue('LABEL');
        const VARTYPE = project.getFeature(variable)?.getSettingValue('DATATYPE');
        const VARNAME = project.getFeature(variable)?.getSettingValue('VARNAME');


        if (!variable) {
            return blocks;
        }

        const links = project.getNodeLinks(this, 'lcd_print_card');
        const doneLinks = project.getNodeLinks(this, 'done');

        const doneBlock = doneLinks.length > 0 ? `'****${doneLinks[0].getSourceId()}****'` : '';

        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `
    'lcd.forecolor=${color}
    'lcd.fill(${x},${y},${w},${h})
    romfile.open(${font})
    lcd.setfont(romfile.offset)
    lcd.textorientation=PL_LCD_TEXT_ORIENTATION_0
    lcd.textverticalspacing=1
    lcd.texthorizontalspacing=1
    lcd.forecolor=${color}
    lcd.backcolor=${backgroundColor}
    ${
    displayLabel === '1' ? `    
    lcd.textalignment=PL_LCD_TEXT_ALIGNMENT_TOP_LEFT
    lcd.printaligned("${label}", ${x * 32}, ${y * 24}, ${w * 32}, ${h * 24 / 2})
    lcd.textalignment=PL_LCD_TEXT_ALIGNMENT_BOTTOM_RIGHT
    lcd.printaligned(${project.convertDataType(VARNAME, VARTYPE, TibboDataType.STRING)}, ${x * 32}, ${y * 24 + h * 24 / 2}, ${w * 32}, ${h * 24 / 2})
    ` : `
    lcd.textalignment=PL_LCD_TEXT_ALIGNMENT_MIDDLE_CENTER
    lcd.printaligned(${project.convertDataType(VARNAME, VARTYPE, TibboDataType.STRING)}, ${x * 32}, ${y * 24}, ${w * 32}, ${h * 24})
`
}
    ${doneBlock}`,
            });
        }


        return blocks;
    }

    getDetails(): string {
        return 'Updates the selected LCD card widget with the value of the selected variable.';
    }
}
